import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useNotification } from "../common/NotificationContext";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMars, faVenus } from "@fortawesome/free-solid-svg-icons";

// Fungsi untuk mengkapitalkan huruf pertama setiap kata
const capitalizeWords = (str) => {
  if (!str || typeof str !== "string") return "";
  return str
    .trim()
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Fungsi untuk mengonversi waktu HH:MM:SS ke detik untuk sorting numerik
const timeToSeconds = (time) => {
  if (!time) return Infinity;
  const [h, m, s] = time.split(":").map(Number);
  return h * 3600 + m * 60 + s;
};

// Fungsi untuk mengonversi kode CIO atau nama negara ke kode ISO untuk bendera
const getFlagCode = (nationality) => {
  if (!nationality) return "un";
  const codeMap = {
    INA: "id",
    INDONESIA: "id",
    GERMANY: "de",
    RUSSIA: "ru",
    FRANCE: "fr",
    USA: "us",
    GBR: "gb",
    MALAYSIA: "my",
    MY: "my",
    JAPAN: "jp",
    JPN: "jp",
  };
  return codeMap[nationality.toUpperCase()] || "un";
};

// Fungsi untuk mengonversi gender ke format dengan ikon
const formatGender = (gender, isMixGroup) => {
  if (isMixGroup) {
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" />
        <FontAwesomeIcon icon={faVenus} className="mr-1" />
        MIX
      </>
    );
  }
  if (!gender) return "-";
  if (gender === "M" || gender === "Laki-laki")
    return (
      <>
        <FontAwesomeIcon icon={faMars} className="mr-1" /> Male
      </>
    );
  if (gender === "F" || gender === "Perempuan")
    return (
      <>
        <FontAwesomeIcon icon={faVenus} className="mr-1" /> Female
      </>
    );
  return gender;
};

// Komponen untuk baris skeleton saat loading
const SkeletonRow = React.memo(() => (
  <tr className="border-b border-gray-200">
    {Array(7)
      .fill()
      .map((_, index) => (
        <td key={index} className="px-4 py-4">
          <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
        </td>
      ))}
  </tr>
));

// Komponen untuk tombol kategori
const CategoryButton = React.memo(({ category, selectedCategory, onClick }) => (
  <button
    onClick={() => onClick(category.distance)}
    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
      selectedCategory === category.distance
        ? "bg-lime-600 text-white"
        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
    }`}
    aria-label={`Filter hasil berdasarkan kategori ${category.displayName}`}
  >
    {category.displayName}
  </button>
));

// Komponen untuk tabel top 3
const Top3Table = ({ title, results, isMixGroup }) => (
  <div className="w-full md:w-[calc(50%-0.5rem)] bg-white rounded-xl border border-gray-300 overflow-x-auto">
    <h2 className="text-lg font-semibold text-lime-600 mb-4 px-4 pt-4">
      {title}
    </h2>

    <table className="min-w-full table-auto border-collapse">
      <thead className="bg-gray-50 border-y border-gray-300">
        <tr>
          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[60px]">
            Rank
          </th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Time
          </th>
          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
            Name
          </th>
        </tr>
      </thead>

      <tbody className="bg-white divide-y divide-gray-200">
        {results.length === 0 ? (
          <tr>
            <td
              colSpan={3}
              className="px-4 py-2 text-center text-sm text-gray-600"
            >
              No data available
            </td>
          </tr>
        ) : (
          results.map((result, index) => (
            <tr
              key={`top3-${result.id || index}`}
              className="hover:bg-gray-50 transition-colors"
            >
              <td className="px-2 py-2 text-sm text-gray-600 text-center w-[50px]">
                {result.genderRanking || result.displayRanking}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600 font-bold w-[100px]">
                {result.time || "-"}
              </td>
              <td className="px-4 py-2 text-sm text-gray-600 uppercase max-w-[150px] truncate">
                {isMixGroup
                  ? capitalizeWords(result.teamName)?.toUpperCase() ||
                    "UNKNOWN TEAM"
                  : capitalizeWords(
                      `${result.firstName} ${result.familyName || ""}`
                    )?.toUpperCase() || "-"}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

export const Results = () => {
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [state, setState] = useState({
    raceResults: [],
    top3Male: [],
    top3Female: [],
    loading: true,
    totalResults: 0,
    selectedCategory: "7-vertical",
    categories: [],
    categoriesLoaded: false,
    bibNumberCache: new Map(),
  });

  const db = getFirestore();
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const mapDistanceFromRegistration = (regDistance, categoryNameFromReg) => {
    const dist = String(regDistance).trim();
    if (dist === "7") {
      if (categoryNameFromReg?.includes("Vertical")) return "7-vertical";
      if (categoryNameFromReg?.includes("Fun Trail")) return "7-fun";
      return "7-vertical";
    } else if (dist === "20") {
      if (categoryNameFromReg?.includes("Classic Distance"))
        return "20-classic";
      if (categoryNameFromReg?.includes("Mix Group")) return "20-mix";
      return "20-classic";
    }
    return dist;
  };

  const fetchAllBibNumbers = async () => {
    try {
      const participantsCollection = collection(db, "pendaftaranJOTR");
      const participantsSnapshot = await getDocs(participantsCollection);
      const bibNumberCache = new Map();
      participantsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data.kategori === "Mix Group (20K)") {
          const bibNumbers = [
            data.bibNumber || "-",
            ...(data.participants || [])
              .slice(0, 2)
              .map((p) => p.bibNumber || "-"),
          ]
            .slice(0, 3)
            .join(", ");
          bibNumberCache.set(doc.id, {
            bibNumber: bibNumbers || "-",
            teamName: capitalizeWords(data.teamName) || "-",
            nationality:
              data.participants?.[0]?.negara || data.negara || "Indonesia",
          });
        } else {
          bibNumberCache.set(doc.id, {
            bibNumber: data.bibNumber || "-",
            teamName: null,
            nationality: data.negara || "Indonesia",
          });
        }
      });
      return bibNumberCache;
    } catch (err) {
      console.error("[ERROR] Failed to fetch bibNumbers:", err);
      showNotification("error", "Failed to load bib numbers.");
      return new Map();
    }
  };

  const fetchCategories = useCallback(async () => {
    try {
      const eventRef = doc(db, "semuaKegiatan", "JOTR");
      const eventDoc = await getDoc(eventRef);
      if (eventDoc.exists()) {
        const rawCategories = eventDoc.data().categories || [];
        const mappedCategories = [];
        const seenDistances = new Set();

        rawCategories.forEach((cat) => {
          let distance = cat.distance.toString().trim();
          let displayName = cat.categoryName;
          let categoryName = cat.categoryName;
          let description = cat.description || cat.categoryName;

          if (cat.categoryName === "Vertical (7K)") {
            distance = "7-vertical";
          } else if (cat.categoryName === "Fun Trail (7K)") {
            distance = "7-fun";
          } else if (cat.categoryName === "Classic Distance (20K)") {
            distance = "20-classic";
            displayName = "Classic Distance (20K)";
            categoryName = "Classic Distance (20K)";
          } else if (cat.categoryName === "Mix Group (20K)") {
            distance = "20-mix";
            displayName = "Mix Group (20K)";
            categoryName = "Mix Group (20K)";
          } else if (cat.categoryName === "Short Distance (40K)") {
            distance = "40";
            displayName = "Short Distance (40K)";
          } else if (cat.categoryName === "Long Distance (80 K)") {
            distance = "80";
            displayName = "Long Distance (80 K)";
          }

          if (!seenDistances.has(distance)) {
            seenDistances.add(distance);
            mappedCategories.push({
              distance,
              displayName,
              description,
              categoryName,
            });
          }
        });

        const sortedCategories = [
          ...mappedCategories.filter((cat) => cat.distance === "7-vertical"),
          ...mappedCategories.filter((cat) => cat.distance === "7-fun"),
          ...mappedCategories.filter((cat) => cat.distance === "20-classic"),
          ...mappedCategories.filter((cat) => cat.distance === "20-mix"),
          ...mappedCategories.filter((cat) => cat.distance === "40"),
          ...mappedCategories.filter((cat) => cat.distance === "80"),
        ];

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            categories: sortedCategories,
            categoriesLoaded: true,
            selectedCategory: sortedCategories.some(
              (cat) => cat.distance === prev.selectedCategory
            )
              ? prev.selectedCategory
              : sortedCategories[0]?.distance || "7-vertical",
          }));
        }
      } else {
        showNotification(
          "error",
          "JOTR document not found. Using default categories."
        );
      }
    } catch (err) {
      console.error("[ERROR] Failed to fetch categories:", err);
      showNotification("error", "Failed to load categories. Using default.");
    }
  }, [showNotification]);

  const loadRaceData = useCallback(
    async (selectedDistance = state.selectedCategory) => {
      if (!state.categoriesLoaded || state.categories.length === 0) {
        console.warn("Categories not loaded yet, skipping loadRaceData");
        return;
      }

      setState((prev) => ({ ...prev, loading: true }));
      try {
        const bibNumberCache = await fetchAllBibNumbers();
        const subCollectionRef = collection(
          db,
          "resultsJOTR",
          selectedDistance,
          "results"
        );
        const resultsSnapshot = await getDocs(subCollectionRef);
        const category = state.categories.find(
          (cat) => cat.distance === selectedDistance
        );

        if (!category) {
          console.error(`Category not found for distance: ${selectedDistance}`);
          showNotification("error", "Category not found.");
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }

        let resultsList = resultsSnapshot.docs.map((doc) => {
          const data = doc.data();
          const bibData = bibNumberCache.get(data.orderId) || {
            bibNumber: "-",
            teamName: data.teamName ? capitalizeWords(data.teamName) : null,
            nationality: data.nationality || "Indonesia",
          };
          return {
            id: doc.id,
            ...data,
            categoryName: category.categoryName,
            bibNumber: bibData.bibNumber,
            teamName: bibData.teamName || data.teamName,
            nationality: bibData.nationality,
          };
        });

        // Calculate gender rankings and overall rankings
        if (selectedDistance !== "20-mix") {
          const maleResults = resultsList
            .filter(
              (result) =>
                result.gender === "M" && result.ranking !== "DNF" && result.time
            )
            .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .map((result, index) => ({
              ...result,
              genderRanking: index + 1,
            }));

          const femaleResults = resultsList
            .filter(
              (result) =>
                result.gender === "F" && result.ranking !== "DNF" && result.time
            )
            .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .map((result, index) => ({
              ...result,
              genderRanking: index + 1,
            }));

          // Combine and sort by overall ranking
          resultsList = [
            ...maleResults,
            ...femaleResults,
            ...resultsList
              .filter(
                (result) =>
                  result.ranking === "DNF" || !result.time || !result.gender
              )
              .map((result) => ({
                ...result,
                genderRanking: "-",
              })),
          ].sort((a, b) => {
            const rankA =
              a.ranking === "DNF" ? Infinity : parseInt(a.ranking) || Infinity;
            const rankB =
              b.ranking === "DNF" ? Infinity : parseInt(b.ranking) || Infinity;
            return rankA - rankB;
          });

          // Assign displayRanking based on sorted order
          resultsList = resultsList.map((result, index) => ({
            ...result,
            displayRanking: result.ranking === "DNF" ? "DNF" : index + 1,
          }));
        } else {
          // For 20-mix, assign displayRanking and no gender ranking
          resultsList = resultsList
            .filter((result) => result.time && result.ranking !== "DNF")
            .sort((a, b) => timeToSeconds(a.time) - timeToSeconds(b.time))
            .map((result, index) => ({
              ...result,
              displayRanking: index + 1,
              genderRanking: "-",
            }))
            .concat(
              resultsList
                .filter((result) => !result.time || result.ranking === "DNF")
                .map((result) => ({
                  ...result,
                  displayRanking: "DNF",
                  genderRanking: "-",
                }))
            );
        }

        // Extract top 3 for non-mix categories
        let top3Male = [];
        let top3Female = [];
        if (selectedDistance !== "20-mix") {
          top3Male = resultsList
            .filter(
              (result) =>
                result.gender === "M" && result.ranking !== "DNF" && result.time
            )
            .slice(0, 3)
            .map((result) => ({
              ...result,
              genderRanking: result.genderRanking,
            }));

          top3Female = resultsList
            .filter(
              (result) =>
                result.gender === "F" && result.ranking !== "DNF" && result.time
            )
            .slice(0, 3)
            .map((result) => ({
              ...result,
              genderRanking: result.genderRanking,
            }));
        }

        if (isMounted.current) {
          setState((prev) => ({
            ...prev,
            raceResults: resultsList,
            totalResults: resultsSnapshot.size,
            top3Male,
            top3Female,
            loading: false,
            bibNumberCache,
          }));
        }
      } catch (err) {
        console.error("[ERROR] Error fetching race data:", err);
        if (isMounted.current) {
          setState((prev) => ({ ...prev, loading: false }));
          showNotification(
            "error",
            "Failed to load race results. Please try again."
          );
        }
      }
    },
    [
      showNotification,
      state.selectedCategory,
      state.categories,
      state.categoriesLoaded,
    ]
  );

  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const handleCategoryChange = useCallback(
    debounce((distance) => {
      if (distance === state.selectedCategory) return;

      if (
        !state.categoriesLoaded ||
        !state.categories.some((cat) => cat.distance === distance)
      ) {
        console.warn(`Invalid or unloaded category: ${distance}`);
        showNotification("error", "Category not loaded or invalid.");
        return;
      }

      setState((prev) => ({
        ...prev,
        selectedCategory: distance,
        raceResults: [],
        totalResults: 0,
        loading: true,
        top3Male: [],
        top3Female: [],
      }));
      loadRaceData(distance);
    }, 300),
    [
      state.selectedCategory,
      state.categories,
      state.categoriesLoaded,
      showNotification,
    ]
  );

  const handleNameClick = (result) => {
    const name =
      state.selectedCategory === "20-mix"
        ? capitalizeWords(result.teamName)?.toUpperCase() || "-"
        : capitalizeWords(
            `${result.firstName} ${result.familyName || ""}`
          )?.toUpperCase() || "-";
    navigate(`/results/${state.selectedCategory}/${encodeURIComponent(name)}`, {
      state: {
        name,
        ranking: result.displayRanking || "-", // Use displayRanking for overall rank
        category: state.selectedCategory,
        bibNumber: result.bibNumber || "-",
        nationality: result.nationality || "Indonesia",
        gender: result.gender || "-",
        participants: result.participants || [],
        status: result.time ? "FINISHED" : "DNF",
        time: result.time || "-",
        genderRanking: result.genderRanking || "-",
      },
    });
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  useEffect(() => {
    if (state.categoriesLoaded && state.categories.length > 0) {
      loadRaceData(state.selectedCategory);
    }
  }, [
    state.categoriesLoaded,
    state.categories,
    state.selectedCategory,
    loadRaceData,
  ]);

  return (
    <section className="min-h-screen py-20 bg-white">
      <div className="container mx-auto px-4">
        <h1 className="text-2xl font-bold text-lime-600 mb-6">
          JOTR Race Results
        </h1>

        <div className="mb-6 flex flex-wrap gap-2">
          {state.categories.map((category) => (
            <CategoryButton
              key={category.distance}
              category={category}
              selectedCategory={state.selectedCategory}
              onClick={handleCategoryChange}
            />
          ))}
        </div>

        {state.selectedCategory !== "20-mix" && (
          <div className="flex flex-col md:flex-row gap-4 mb-4">
            <Top3Table
              title="Top 3 Men"
              results={state.top3Male}
              isMixGroup={false}
            />
            <Top3Table
              title="Top 3 Women"
              results={state.top3Female}
              isMixGroup={false}
            />
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-300 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-auto">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[50px]">
                  Rank
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">
                  Time
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bib
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                  Country
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider max-w-[150px]">
                  Gender
                </th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {state.loading ? (
                <SkeletonRow />
              ) : state.raceResults.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-4 text-center text-sm text-gray-600"
                  >
                    No race results available for this category.
                  </td>
                </tr>
              ) : (
                state.raceResults.map((result, index) => (
                  <tr
                    key={`${result.id || "unknown"}-${index}`}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-2 py-4 text-sm text-gray-600 text-center w-[60px]">
                      {result.displayRanking || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 font-bold">
                      {result.time || "-"}
                    </td>
                    <td
                      className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 uppercase max-w-[150px] truncate cursor-pointer hover:text-lime-600"
                      onClick={() => handleNameClick(result)}
                    >
                      {state.selectedCategory === "20-mix"
                        ? capitalizeWords(result.teamName)?.toUpperCase() || "-"
                        : capitalizeWords(
                            `${result.firstName} ${result.familyName || ""}`
                          )?.toUpperCase() || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600">
                      <span className="border border-gray-300 rounded-full px-2 py-1">
                        {result.bibNumber ? `BIB ${result.bibNumber}` : "-"}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 max-w-[150px]">
                      <span className="border border-gray-300 rounded-full px-2 py-1 inline-flex items-center">
                        <img
                          src={`https://flagicons.lipis.dev/flags/4x3/${getFlagCode(
                            result.nationality
                          )}.svg`}
                          className="country-flag w-4 h-3 mr-1 border border-gray-300 rounded"
                          alt={result.nationality}
                        />
                        {result.nationality || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 max-w-[150px]">
                      <span className="border border-gray-300 rounded-full px-2 py-1">
                        {formatGender(
                          result.gender,
                          state.selectedCategory === "20-mix"
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-xs text-lime-600">
                      <span className="bg-lime-50 border border-lime-400 rounded-full px-2 py-1">
                        FINISHED
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};
